import { useCallback, useEffect, useState } from 'react';
import { vars } from 'src/core/themes';
import { Button, Stack } from 'src/core/ui';
import { GraduationCapIcon } from 'src/svgs/GraduationCapIcon';

import * as styles from './BoostLessonsPopup.css';

interface Lesson {
  title: string;
  videoUrl: string;
  description: string;
  hidden?: boolean;
}

const LESSONS: Lesson[] = [
  {
    title: 'Introducing Boost',
    videoUrl:
      'https://img.vggcdn.net/videos/sponsoredListings/IntroductionToBoost.mp4',
    description:
      'This tutorial is your starting point for **Boost**—the Reach capability in Sponsored Listings that helps your tickets win premium placement on StubHub.com.',
  },
  {
    title: 'Boost Settings',
    videoUrl:
      'https://img.vggcdn.net/videos/sponsoredListings/BoostSettings.mp4',
    description:
      "In this tutorial, you'll learn how to configure **Boost** so it aligns with your selling strategy and goals on StubHub.com.",
  },
  {
    title: 'Boost Management',
    videoUrl:
      'https://img.vggcdn.net/videos/sponsoredListings/BoostManagement.mp4',
    description:
      "Once your Boost campaigns are live, ongoing management is key to maximizing performance. In this tutorial, you'll learn how to monitor results and make data-driven decisions to grow your sales efficiently.",
  },
  {
    title: 'Strategies',
    videoUrl:
      'https://img.vggcdn.net/videos/sponsoredListings/BidStrategies.mp4',
    description:
      'Strategies helps you balance spend with opportunities throughout the day to reach more StubHub buyers.',
  },
  {
    title: 'Automated Campaigns',
    videoUrl:
      'https://img.vggcdn.net/videos/sponsoredListings/AutomatedCampaigns.mp4',
    description:
      "In this tutorial, you'll learn how **automated campaigns** do the heavy lifting for you — Boost manages your bids based on rules you set, keeping your listings competitive while you focus on selling.",
    hidden: true,
  },
];

const VISIBLE_LESSONS = LESSONS.filter((lesson) => !lesson.hidden);

const STORAGE_KEY = 'sponsoredListings-lessonsPopup-collapsed';

const getInitialCollapsedState = (): boolean => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return false; // Default to open on first visit
    }
    return JSON.parse(stored) === true;
  } catch (e) {
    // Handle localStorage access errors (private mode, disabled, quota exceeded)
    console.warn('Failed to read from localStorage:', e);
    return false;
  }
};

const persistCollapsedState = (isCollapsed: boolean) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(isCollapsed));
  } catch (e) {
    console.warn('Failed to write to localStorage:', e);
  }
};

// Helper function to render description with bold text
const renderDescription = (description: string) => {
  const parts = description.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      const text = part.slice(2, -2);
      return <strong key={index}>{text}</strong>;
    }
    return part;
  });
};

export function BoostLessonsPopup() {
  const [isCollapsed, setIsCollapsed] = useState(getInitialCollapsedState);
  const [currentLessonIndex, setCurrentLessonIndex] = useState(0);
  const [videoBlobUrl, setVideoBlobUrl] = useState<string | null>(null);
  const [isVideoLoading, setIsVideoLoading] = useState(false);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const currentLesson = VISIBLE_LESSONS[currentLessonIndex];
  const isLastLesson = currentLessonIndex === VISIBLE_LESSONS.length - 1;

  useEffect(() => {
    persistCollapsedState(isCollapsed);
  }, [isCollapsed]);

  // Fetch video as blob to force full download before playback
  useEffect(() => {
    if (isCollapsed) {
      return;
    }

    const abortController = new AbortController();
    let blobUrl: string | null = null;

    const fetchVideo = async () => {
      setIsVideoLoading(true);
      setVideoError(null);
      setVideoBlobUrl((prev) => {
        if (prev) {
          URL.revokeObjectURL(prev);
        }
        return null;
      });

      try {
        const response = await fetch(currentLesson.videoUrl, {
          signal: abortController.signal,
        });

        if (!response.ok) {
          throw new Error(`Failed to download video (${response.status})`);
        }

        const blob = await response.blob();
        blobUrl = URL.createObjectURL(blob);
        setVideoBlobUrl(blobUrl);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          return; // Fetch was cancelled — let the new effect manage loading state
        }
        setVideoError(
          err instanceof Error ? err.message : 'Failed to load video'
        );
      } finally {
        if (!abortController.signal.aborted) {
          setIsVideoLoading(false);
        }
      }
    };

    fetchVideo();

    return () => {
      abortController.abort();
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [currentLesson.videoUrl, isCollapsed, retryCount]);

  const handleDismiss = useCallback(() => {
    setIsCollapsed(true);
  }, []);

  const handleExpand = useCallback(() => {
    setIsCollapsed(false);
  }, []);

  const handleNextLesson = useCallback(() => {
    if (!isLastLesson) {
      setCurrentLessonIndex((prev) => prev + 1);
    }
  }, [isLastLesson]);

  const handleDotClick = useCallback((index: number) => {
    setCurrentLessonIndex(index);
  }, []);

  if (isCollapsed) {
    return (
      <button
        className={styles.collapsedButton}
        onClick={handleExpand}
        type="button"
      >
        <GraduationCapIcon
          size={vars.iconSize.xl}
          stroke={vars.color.iconPrimaryInverted}
        />
        <span>Get Started with Boost</span>
      </button>
    );
  }

  return (
    <div className={styles.popupContainer}>
      <div className={styles.popupContent}>
        <div className={styles.videoContainer}>
          {isVideoLoading && (
            <div className={styles.videoLoadingOverlay}>
              <div className={styles.spinner} />
              <p className={styles.loadingText}>Loading video…</p>
            </div>
          )}
          {videoError && !isVideoLoading && (
            <div className={styles.videoErrorContainer}>
              <p className={styles.errorText}>{videoError}</p>
              <Button
                variant="outline"
                onClick={() => setRetryCount((c) => c + 1)}
              >
                Retry
              </Button>
            </div>
          )}
          {videoBlobUrl && !isVideoLoading && !videoError && (
            // eslint-disable-next-line jsx-a11y/media-has-caption -- Tutorial videos are hosted externally without caption tracks
            <video
              key={currentLesson.videoUrl}
              className={styles.video}
              controls
              playsInline
              src={videoBlobUrl}
            />
          )}
        </div>

        <Stack direction="column" gap="m" alignItems="center">
          <h3 className={styles.lessonTitle}>{currentLesson.title}</h3>
          <p className={styles.lessonDescription}>
            {renderDescription(currentLesson.description)}
          </p>
        </Stack>

        <div className={styles.dotIndicators}>
          {VISIBLE_LESSONS.map((_, index) => (
            <button
              key={index}
              className={`${styles.dot} ${
                index === currentLessonIndex ? styles.dotActive : ''
              }`}
              onClick={() => handleDotClick(index)}
              aria-label={`Go to lesson ${index + 1}`}
              type="button"
            />
          ))}
        </div>

        <div className={styles.buttonContainer}>
          <Button variant="outline" onClick={handleDismiss}>
            Dismiss
          </Button>
          {!isLastLesson && (
            <Button variant="regular" onClick={handleNextLesson}>
              Next Lesson
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
